/**
 * Iter-234f — AI Visibility Pass.
 *
 * Tiny reusable badges + footer so the AI features iEvntür already
 * powers are visible to users. Used across Staffing roster, marketplace
 * cards, AI-generated content, etc.
 *
 * The goal is to surface what's already running silently in the
 * platform without adding any new ML calls or UI clutter.
 */
import React from 'react';
import {
  Sparkles, Bot, ShieldCheck, Lightbulb, Tooltip as TooltipIcon, Info,
} from 'lucide-react';

/** Tone tokens — keeps the look consistent across the app. */
const TONE = {
  cyan:    'bg-cyan-50 text-cyan-700 border-cyan-200',
  violet:  'bg-violet-50 text-violet-700 border-violet-200',
  emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  amber:   'bg-amber-50 text-amber-700 border-amber-200',
};

/**
 * Compact "AI Match: 87" pill for applicant + talent cards.
 * Wires hover-text to surface the top reasons the model picked them.
 */
export function AIMatchScoreBadge({ score, reasons = [], size = 'sm', testId = 'ai-match-score' }) {
  if (typeof score !== 'number') return null;
  const tone = score >= 85 ? TONE.emerald : score >= 65 ? TONE.cyan : TONE.amber;
  const padding = size === 'sm' ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-1 text-xs';
  return (
    <span
      className={`inline-flex items-center gap-0.5 font-semibold rounded-full border ${tone} ${padding}`}
      title={reasons.length ? `AI match reasons: ${reasons.join(' · ')}` : `AI match score: ${score}/100`}
      data-testid={testId}
    >
      <Bot className={size === 'sm' ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
      AI Match {score}
    </span>
  );
}

/**
 * "🤖 AI Pick" chip for marketplace cards where the ranker placed
 * the card in the top decile (top ~10%).
 */
export function AIPickBadge({ size = 'sm', testId = 'marketplace-ai-pick' }) {
  return (
    <span
      className={`inline-flex items-center gap-0.5 font-semibold rounded-full border ${TONE.cyan} ${
        size === 'sm' ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-1 text-xs'
      }`}
      title="iEvntür AI placed this in your top picks based on match quality."
      data-testid={testId}
    >
      <Bot className="w-2.5 h-2.5" /> AI Pick
    </span>
  );
}

/**
 * Footer for AI-generated content (description, dress code, notes).
 * Stays subtle so it doesn't compete with the content.
 */
export function AIGeneratedFooter({ label = 'Drafted with iEvntür AI · edit freely', testId = 'ai-generated-footer' }) {
  return (
    <p
      className="mt-1 text-[10px] text-slate-500 flex items-center gap-1"
      data-testid={testId}
    >
      <Sparkles className="w-3 h-3 text-cyan-500" />
      <span>{label}</span>
    </p>
  );
}

/**
 * Inline rename helper — "Reliability Score" → "AI Reliability Score"
 * with hover tooltip explaining the 5 signals.
 */
export function AIReliabilityLabel({ className = '', testId = 'ai-reliability-label' }) {
  return (
    <span
      className={`inline-flex items-center gap-1 ${className}`}
      title="Reliability Score is generated by iEvntür AI from 5 signals: on-time arrival %, no-show %, talent ratings, cancellation history, and shifts completed."
      data-testid={testId}
    >
      <ShieldCheck className="w-3.5 h-3.5 text-cyan-600" />
      AI Reliability Score
      <Info className="w-3 h-3 text-slate-400" />
    </span>
  );
}

/**
 * "AI auto-filled" indicator for Smart Replacement / standby fills.
 * Use on booking rows where booked_by was the smart-replacement system.
 */
export function AIAutoFilledBadge({ testId = 'ai-auto-filled' }) {
  return (
    <span
      className={`inline-flex items-center gap-0.5 font-semibold rounded-full border px-1.5 py-0.5 text-[10px] ${TONE.violet}`}
      title="iEvntür AI auto-filled this slot from your backup roster."
      data-testid={testId}
    >
      <Bot className="w-2.5 h-2.5" /> AI Auto-Filled
    </span>
  );
}
